import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { MapPin, Store, ArrowRight, Search, Tag, Crown } from "lucide-react";

export default function GoogleListings() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useSeo({
    title: "Fiches Google des partenaires — Annuaire des commerces en Eure-et-Loir | PROXIreduc-28",
    description: "L'annuaire complet des commerces et artisans partenaires de PROXIreduc-28 en Eure-et-Loir. Trouvez coordonnées, horaires et bons plans près de chez vous à Chartres, Dreux, Châteaudun…",
    canonicalPath: "/fiches-google",
    image: "/og-fiches-google.jpg",
  });

  useEffect(() => {
    api.get("/public/partners?limit=120").then((r) => {
      setItems(r.data.items || []);
      setTotal(r.data.total || (r.data.items || []).length);
    }).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return items;
    return items.filter((b) =>
      (b.name || "").toLowerCase().includes(t) ||
      (b.city || "").toLowerCase().includes(t) ||
      (b.category?.name || "").toLowerCase().includes(t)
    );
  }, [items, q]);

  // Regroupement par ville (insensible à la casse) pour un maillage SEO clair
  const groups = useMemo(() => {
    const map = {};
    filtered.forEach((b) => {
      const raw = (b.city || "Autres").trim();
      const key = raw.toLowerCase();
      if (!map[key]) map[key] = { label: raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase(), list: [] };
      map[key].list.push(b);
    });
    return Object.values(map).sort((a, b) => a.label.localeCompare(b.label, "fr"));
  }, [filtered]);

  return (
    <div data-testid="google-listings-page">
      <section className="bg-gradient-to-br from-slate-50 via-white to-orange-50/40 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#E05638]">
            <Store size={14} /> Annuaire des partenaires
          </div>
          <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-extrabold text-slate-900">
            Fiches Google des commerces partenaires
          </h1>
          <p className="mt-3 text-base text-slate-600 max-w-2xl">
            Retrouvez toutes les fiches de nos commerces et artisans partenaires en Eure-et-Loir :
            coordonnées, horaires, avis et bons plans. Chaque fiche est optimisée pour être trouvée
            facilement sur Google, près de chez vous.
          </p>

          <div className="mt-6 relative max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="google-listings-search"
              placeholder="Rechercher un commerce, une ville, une catégorie…"
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#E05638]/30 focus:border-[#E05638]"
            />
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="text-slate-500">Chargement…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-500" data-testid="google-listings-empty">
            Aucun partenaire ne correspond à votre recherche.
          </div>
        ) : (
          <div className="space-y-10" data-testid="google-listings-content">
            <p className="text-sm text-slate-500" data-testid="google-listings-count">
              {filtered.length} commerce{filtered.length > 1 ? "s" : ""} partenaire{filtered.length > 1 ? "s" : ""}
              {q.trim() ? "" : ` · ${total} au total`}
            </p>
            {groups.map((g) => (
              <div key={g.label}>
                <h2 className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2 mb-4">
                  <MapPin size={18} className="text-[#E05638]" /> {g.label}
                  <span className="text-sm font-normal text-slate-400">({g.list.length})</span>
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {g.list.map((b) => (
                    <Link
                      key={b.id}
                      to={`/commerce/${b.id}`}
                      data-testid={`google-listing-${b.id}`}
                      className={`group rounded-2xl p-4 flex gap-3 hover:shadow-lg hover:-translate-y-0.5 transition-all ${b.premium ? "bg-amber-50/70 border-2 border-amber-300 shadow-sm" : "bg-white border border-slate-200"}`}
                    >
                      <div className="w-14 h-14 rounded-xl bg-slate-100 overflow-hidden shrink-0 grid place-items-center text-slate-300">
                        {b.logo || b.cover_image
                          ? <img src={b.logo || b.cover_image} alt={b.name} className="w-full h-full object-cover" />
                          : <Store size={22} />}
                      </div>
                      <div className="min-w-0">
                        {b.premium && (
                          <span className="inline-flex items-center gap-1 mb-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full" data-testid={`listing-premium-${b.id}`}>
                            <Crown size={10}/> Partenaire Premium
                          </span>
                        )}
                        <div className="font-bold text-slate-900 truncate group-hover:text-[#E05638] transition-colors">{b.name}</div>
                        <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                          {b.category?.name && <span className="inline-flex items-center gap-1"><Tag size={11} /> {b.category.name}</span>}
                          {b.offer_count > 0 && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-orange-50 text-[#c8462b] font-semibold">
                              {b.offer_count} offre{b.offer_count > 1 ? "s" : ""}
                            </span>
                          )}
                        </div>
                        <span className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-[#E05638] opacity-0 group-hover:opacity-100 transition-opacity">
                          Voir la fiche <ArrowRight size={11} />
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
